/**
 * Central error/telemetry reporter fronting the Sentry SDK.
 *
 * The sink is self-hosted (GlitchTip, Sentry-API-compatible — see
 * docs/observability-runbook.md); no tenant/student data leaves
 * infrastructure we control. App code reports through this module — never
 * import the SDK directly — so the reporter stays swappable and no-ops
 * cleanly when NEXT_PUBLIC_SENTRY_DSN is unset (local dev, tests).
 */

import * as Sentry from '@sentry/nextjs';
import { normalizeHttpOrigin } from '@/lib/security/csp';

export function telemetryEnabled(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN);
}

function stripStorageCapability(value: string): string {
  const storageOrigin = normalizeHttpOrigin(process.env.NEXT_PUBLIC_STORAGE_ORIGIN);
  if (!storageOrigin) return value;

  try {
    const url = new URL(value);
    if (url.origin !== storageOrigin) return value;

    // A presigned query string is a temporary bearer capability. Preserve the
    // object path for diagnostics, but never send its credentials or fragment.
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return value;
  }
}

/** Remove opaque capabilities from page/API URLs captured by the SDK. */
export function redactTelemetryValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return stripStorageCapability(value)
      .replace(/(\/verify\/)[^/?#\s]+/g, '$1[REDACTED]')
      .replace(/(\/api\/public\/certificates\/)[^/?#\s]+/g, '$1[REDACTED]')
      .replace(/(\/api\/public\/placement-tests\/)[^/?#\s]+/g, '$1[REDACTED]')
      .replace(/([?&](?:token|continuation_token|code|state)=)[^&#\s]*/gi, '$1[REDACTED]')
      // Protect AWS capability parameters if a URL is embedded in a larger
      // message or storage is temporarily misconfigured.
      .replace(/([?&]X-Amz-[^=&#\s]+)=[^&#\s]*/gi, '$1=[REDACTED]')
      .replace(/([?&#]request=)lms_mcq_[^&#\s]*/gi, '$1[REDACTED]');
  }
  if (Array.isArray(value)) return value.map(redactTelemetryValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, redactTelemetryValue(entry)])
    );
  }
  return value;
}

/** Called once from instrumentation-client.ts at page bootstrap. Registers
 * the SDK's global handlers (window.onerror, unhandledrejection). */
export function initTelemetry(): void {
  if (!telemetryEnabled()) return;
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    sendDefaultPii: false,
    // Error tracking only; performance tracing is Prometheus/Grafana's job.
    tracesSampleRate: 0,
    beforeSend: (event) => redactTelemetryValue(event) as typeof event,
    beforeBreadcrumb: (breadcrumb) =>
      redactTelemetryValue(breadcrumb) as typeof breadcrumb,
  });
}

/** Capture a handled exception with optional structured context. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (!telemetryEnabled()) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Capture a message-level event (no Error object available). */
export function reportMessage(
  message: string,
  level: 'warning' | 'error' = 'error',
  context?: Record<string, unknown>
): void {
  if (!telemetryEnabled()) return;
  Sentry.captureMessage(message, { level, extra: context });
}

/** Leave a breadcrumb on the current scope so later error events carry the
 * trail. Cheap; safe to call on every noteworthy client action. */
export function addTelemetryBreadcrumb(
  category: string,
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, unknown>
): void {
  if (!telemetryEnabled()) return;
  Sentry.addBreadcrumb({ category, message, level, data });
}

export interface ApiFailureEvent {
  endpoint: string;
  method: string;
  componentName: string;
  requestId: string;
  /** HTTP status; undefined for network-level failures (fetch threw). */
  status?: number;
  /** Machine-readable backend error code, e.g. `feature_not_entitled`. */
  code?: string;
  message?: string;
}

/**
 * Record a failed API call from the central fetch wrapper. Every failure
 * leaves a breadcrumb; 5xx and network-level failures become error events
 * (4xx are expected user/permission flows and would only be noise).
 * `requestId` matches the backend's `x-request-id` log field, so a report
 * joins the exact server log line.
 */
export function reportApiFailure(event: ApiFailureEvent): void {
  if (!telemetryEnabled()) return;

  const label = `${event.method} ${event.endpoint} -> ${event.status ?? 'network-error'}`;
  const data: Record<string, unknown> = {
    endpoint: event.endpoint,
    method: event.method,
    status: event.status,
    code: event.code,
    component: event.componentName,
    request_id: event.requestId,
  };

  addTelemetryBreadcrumb('api.failure', label, 'error', data);

  if (event.status === undefined || event.status >= 500) {
    reportMessage(`API failure: ${label}`, 'error', {
      ...data,
      message: event.message,
    });
  }
}

/** Forward a Web Vitals metric as a breadcrumb (ops signal, not analytics). */
export function reportWebVital(metric: {
  name: string;
  value: number;
  id: string;
  rating?: string;
}): void {
  if (!telemetryEnabled()) return;
  addTelemetryBreadcrumb('web-vital', metric.name, 'info', {
    value: metric.value,
    rating: metric.rating,
    id: metric.id,
  });
}
